import React, { useEffect, useState } from "react";
import { CheckCircle2, Share, SquarePlus, EllipsisVertical, MonitorDown } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useLang } from "@/lib/i18n";

/* Self-hosted inline brand glyphs — zero external assets (spec: no CDNs). */
const AppleLogo = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
    <path d="M17.05 12.54c-.03-2.5 2.04-3.7 2.13-3.76-1.16-1.7-2.97-1.93-3.61-1.96-1.54-.16-3 .9-3.78.9-.78 0-1.98-.88-3.26-.86-1.68.03-3.22.98-4.08 2.48-1.74 3.02-.44 7.49 1.25 9.94.83 1.2 1.82 2.55 3.12 2.5 1.25-.05 1.72-.81 3.23-.81 1.51 0 1.93.81 3.26.79 1.35-.03 2.2-1.22 3.02-2.43.95-1.39 1.34-2.74 1.36-2.81-.03-.01-2.61-1-2.64-3.98zM14.56 4.9c.69-.83 1.15-1.99 1.02-3.15-.99.04-2.18.66-2.89 1.49-.63.73-1.19 1.91-1.04 3.04 1.1.09 2.23-.56 2.91-1.38z" />
  </svg>
);

const AndroidLogo = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
    <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24a11.46 11.46 0 0 0-8.94 0L5.65 5.67a.63.63 0 0 0-.87-.2c-.28.17-.37.53-.22.82L6.4 9.48A10.78 10.78 0 0 0 1 18h22a10.78 10.78 0 0 0-5.4-8.52zM7 15.25a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5zm10 0a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5z" />
  </svg>
);

const isIosDevice = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

/* Prominent "install on your phone" section for the login page.
   - Android/Chrome: uses the captured beforeinstallprompt for a real
     one-tap install; falls back to menu instructions if unavailable.
   - iOS/Safari: Apple provides no install API — shows the required
     Share → Add to Home Screen steps. */
export const InstallApp = () => {
  const { t } = useLang();
  const [dialog, setDialog] = useState(null); // "ios" | "android" | null
  const [installed, setInstalled] = useState(isStandalone());
  const [, setPromptReady] = useState(!!window.__rkInstallPrompt);

  useEffect(() => {
    const onReady = () => setPromptReady(true);
    const onInstalled = () => { setInstalled(true); setDialog(null); };
    window.addEventListener("rk-install-ready", onReady);
    window.addEventListener("rk-installed", onInstalled);
    return () => {
      window.removeEventListener("rk-install-ready", onReady);
      window.removeEventListener("rk-installed", onInstalled);
    };
  }, []);

  const clickAndroid = async () => {
    const p = window.__rkInstallPrompt;
    if (p) {
      try {
        p.prompt();
        const choice = await p.userChoice;
        if (choice?.outcome === "accepted") {
          window.__rkInstallPrompt = null;
          return;
        }
      } catch {
        /* fall through to instructions */
      }
      return; // user dismissed the native prompt — don't nag with a dialog
    }
    setDialog("android"); // prompt not available (e.g. not Chrome) — show steps
  };

  if (installed) {
    return (
      <div data-testid="install-app-installed"
        className="mx-auto mt-6 flex w-full max-w-sm items-center gap-3 rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground">
        <CheckCircle2 className="h-5 w-5 shrink-0 text-primary" />
        {t("installInstalled")}
      </div>
    );
  }

  const steps = dialog === "ios"
    ? [
        { icon: MonitorDown, text: t("installIosStep1") },
        { icon: Share, text: t("installIosStep2") },
        { icon: SquarePlus, text: t("installIosStep3") },
      ]
    : [
        { icon: MonitorDown, text: t("installAndroidStep1") },
        { icon: EllipsisVertical, text: t("installAndroidStep2") },
        { icon: SquarePlus, text: t("installAndroidStep3") },
      ];

  const ios = isIosDevice();

  return (
    <>
      <div data-testid="install-app-section"
        className="mx-auto mt-6 w-full max-w-sm rounded-2xl border border-primary/20 bg-primary/5 p-5">
        <p className="text-sm font-semibold tracking-tight">{t("installAppTitle")}</p>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">{t("installAppSubtitle")}</p>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <button
            type="button"
            data-testid="install-ios-button"
            onClick={() => setDialog("ios")}
            className={`flex h-12 items-center justify-center gap-2 rounded-xl border text-sm font-medium transition-colors duration-150 hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              ios ? "border-primary bg-card shadow-sm" : "border-border bg-card"
            }`}
          >
            <AppleLogo className="h-5 w-5" /> {t("installIos")}
          </button>
          <button
            type="button"
            data-testid="install-android-button"
            onClick={clickAndroid}
            className={`flex h-12 items-center justify-center gap-2 rounded-xl border text-sm font-medium transition-colors duration-150 hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              !ios ? "border-primary bg-card shadow-sm" : "border-border bg-card"
            }`}
          >
            <AndroidLogo className="h-5 w-5" /> {t("installAndroid")}
          </button>
        </div>
      </div>

      <Dialog open={dialog !== null} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent data-testid="install-instructions-dialog" className="max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {dialog === "ios"
                ? <AppleLogo className="h-5 w-5" />
                : <AndroidLogo className="h-5 w-5" />}
              {dialog === "ios" ? t("installIosTitle") : t("installAndroidTitle")}
            </DialogTitle>
            <DialogDescription>{t("installDialogNote")}</DialogDescription>
          </DialogHeader>
          <ol className="mt-2 space-y-4">
            {steps.map(({ icon: Icon, text }, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-sm font-semibold text-primary">
                  {i + 1}
                </span>
                <span className="flex items-center gap-2 pt-1 text-sm leading-6">
                  <Icon className="h-4 w-4 shrink-0 text-primary" /> {text}
                </span>
              </li>
            ))}
          </ol>
          <p className="mt-2 text-xs leading-5 text-muted-foreground">{t("installHint")}</p>
        </DialogContent>
      </Dialog>
    </>
  );
};
